import { Feature } from '@23degrees/shared/models/pricing/features.model';
import { Product } from '@23degrees/shared/models/pricing/products.model';
import { HttpClient } from '@angular/common/http';
import { Injectable, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, share, take, tap } from 'rxjs/operators';
import { EnvironmentService } from '../env/env.service';
import { GlobalService } from '../global/global.service';

interface ProductsResponse {
  action: string;
  status: string;
  data: Product[];
}

interface FeaturesResponse {
  action: string;
  status: string;
  data: Feature[];
}

@Injectable({ providedIn: 'root' })
export class PaymentService2 {
  private httpClient = inject(HttpClient);

  private envService: EnvironmentService = inject(EnvironmentService);

  private products: Product[] = [];
  private features: Feature[] = [];

  private products$: Observable<ProductsResponse>;
  private features$: Observable<FeaturesResponse>;

  private globalService: GlobalService = inject(GlobalService);
  private transferState: TransferState = inject(TransferState);

  ACCESS_CONTROL_PATH = this.envService.ACCESS_CONTROL_PATH;
  ACCESS_CONTROL_PATH_V2 = `${this.ACCESS_CONTROL_PATH}/v2`;

  /**
   * Initial populate products and features to the db
   */
  init() {
    // return this.httpClient.get(`${this.ACCESS_CONTROL_PATH_V2}/init-db`).toPromise();
  }

  /**
   * Fetch products from server and share/multicast if multiple requests
   */
  private fetchProducts() {
    const key = makeStateKey<string>(JSON.stringify({ name: 'fetchProducts' }));

    if (this.globalService.isBrowser() && this.transferState.hasKey(key)) {
      const data = this.transferState.get(key, '');
      this.products$ = of(data as any);
    }

    if (this.products$) {
      return this.products$;
    }
    this.products$ = this.httpClient.get<ProductsResponse>(`${this.ACCESS_CONTROL_PATH_V2}/products`).pipe(
      share(),
      tap((data) => {
        this.transferState.set(key, data as any);
      }),
    );
    return this.products$;
  }

  /**
   * Get products
   */
  public async getProducts(): Promise<Product[]> {
    if (this.products.length > 0) return this.products;
    this.products = (await this.fetchProducts().pipe(take(1)).toPromise()).data;
    return this.products;
  }

  public async getProductsPromise() {
    this.products = (await this.httpClient.get<ProductsResponse>(`${this.ACCESS_CONTROL_PATH_V2}/products`).toPromise()).data;
    return this.products;
  }

  public getProductsObs() {
    return this.httpClient.get<ProductsResponse>(`${this.ACCESS_CONTROL_PATH_V2}/products`).pipe(map((response) => response.data))
  }


  public async putProductsPromise(product) {
    this.products = (await this.httpClient.put<any>(`${this.ACCESS_CONTROL_PATH_V2}/products`, product).toPromise()).data;
    return this.products;
  }

  private fetchFeatures() {
    const key = makeStateKey<string>(JSON.stringify({ name: 'fetchFeatures' }));

    if (this.globalService.isBrowser() && this.transferState.hasKey(key)) {
      const data = this.transferState.get(key, '');
      this.features$ = of(data as any);
    }

    if (this.features$) {
      return this.features$;
    }
    this.features$ = this.httpClient.get<FeaturesResponse>(`${this.ACCESS_CONTROL_PATH_V2}/features`).pipe(
      share(),
      tap((data) => {
        this.transferState.set(key, data as any);
      }),
    );
    return this.features$;
  }

  public async getFeatures(): Promise<Feature[]> {
    if (this.features.length > 0) return this.features;
    this.features = (await this.fetchFeatures().pipe(take(1)).toPromise()).data;
    return this.features;
  }

  public async getFeaturesPromise() {
    return this.httpClient.get<any>(`${this.ACCESS_CONTROL_PATH_V2}/features`).toPromise();
  }

  public getFeaturesObs() {
    return this.httpClient.get<any>(`${this.ACCESS_CONTROL_PATH_V2}/features`)
  }


  // getFeaturesByProduct(productId: string, full: boolean = true): Promise<Feature[]> {
  //   return this.httpClient.get<Feature[]>(`${this.ACCESS_CONTROL_PATH_V2}/products/${productId}/features/${full}`).toPromise();
  // }

  public async getProductsByFeature(featureId: string): Promise<Product[]> {
    const ret = await this.httpClient
      .get<ProductsResponse>(`${this.ACCESS_CONTROL_PATH_V2}/features/${featureId}/products`)
      .toPromise()
      .catch((error) => {
        console.log('[getProductsByFeature]', 'error', error);
      });

    if (ret) {
      return ret.data;
    }

    return [];
  }

  public async getProductsByUser(): Promise<Product[]> {
    const ret = await this.httpClient
      .get<ProductsResponse>(`${this.ACCESS_CONTROL_PATH_V2}/user/products`)
      .toPromise()
      .catch((error) => {
        console.log('[getProductsByUser]', 'error', error);
      });

    if (ret) {
      return ret.data;
    }

    return [];
  }

  public postFeatures(feature) {
    return this.httpClient.post<any>(`${this.ACCESS_CONTROL_PATH_V2}/features`, feature).toPromise();
  }

  public putFeatures(feature) {
    return this.httpClient.put<any>(`${this.ACCESS_CONTROL_PATH_V2}/features`, feature).toPromise();
  }
}
